import { useState, useRef, useEffect } from 'react'
import {
    getAuth,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    type AuthError,
} from 'firebase/auth'
import { app } from '../../firebase'
import { translations, type Language } from '../../translations'

interface AuthModalProps {
    isOpen: boolean
    onClose: () => void
    lang: Language
}

type AuthMode = 'signin' | 'signup' | 'verify'

// Minimum time (ms) a human needs to fill the form - bots are faster
const MIN_FORM_TIME_MS = 2000

export function AuthModal({ isOpen, onClose, lang }: AuthModalProps) {
    const t = translations[lang]
    const isRTL = lang === 'ar'
    const auth = getAuth(app)

    const [mode, setMode] = useState<AuthMode>('signin')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    // Bot protection: honeypot field and timing
    const [honeypot, setHoneypot] = useState('')
    const formLoadTime = useRef<number>(Date.now())
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [verificationEmail, setVerificationEmail] = useState('')

    // Reset timer when modal opens
    useEffect(() => {
        if (isOpen) {
            formLoadTime.current = Date.now()
            setHoneypot('')
        }
    }, [isOpen])

    if (!isOpen) return null

    const getErrorMessage = (errorCode: string): string => {
        switch (errorCode) {
            case 'auth/invalid-email':
                return t.invalidEmail
            case 'auth/email-already-in-use':
                return t.emailInUse
            case 'auth/wrong-password':
            case 'auth/invalid-credential':
                return t.wrongPassword
            case 'auth/user-not-found':
                return t.userNotFound
            case 'auth/weak-password':
                return t.passwordTooShort
            default:
                return t.errorGeneric
        }
    }

    const handleGoogleSignIn = async () => {
        setLoading(true)
        setError('')
        try {
            const provider = new GoogleAuthProvider()
            await signInWithPopup(auth, provider)
            onClose()
        } catch (err) {
            const authError = err as AuthError
            setError(getErrorMessage(authError.code))
        } finally {
            setLoading(false)
        }
    }

    const handleEmailSignIn = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        if (!email || !password) return

        setLoading(true)
        try {
            const result = await signInWithEmailAndPassword(auth, email, password)

            // Check if email is verified
            if (!result.user.emailVerified) {
                // Show verify screen (don't auto-send email to avoid 400 rate-limit errors)
                setVerificationEmail(email)
                setMode('verify')
                setLoading(false)
                return
            }

            onClose()
        } catch (err) {
            const authError = err as AuthError
            setError(getErrorMessage(authError.code))
        } finally {
            setLoading(false)
        }
    }

    const handleEmailSignUp = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        if (!email || !password || !confirmPassword) return

        // Validate passwords match
        if (password !== confirmPassword) {
            setError(t.passwordMismatch)
            return
        }

        // Validate password length
        if (password.length < 6) {
            setError(t.passwordTooShort)
            return
        }

        // Bot protection: honeypot filled or form submitted too fast
        const timeElapsed = Date.now() - formLoadTime.current
        if (honeypot || timeElapsed < MIN_FORM_TIME_MS) {
            // Silently reject - don't give bots feedback
            console.warn('Bot detected:', honeypot ? 'honeypot filled' : 'too fast')
            return
        }

        setLoading(true)
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password)

            // Send verification email
            await sendEmailVerification(result.user)
            setVerificationEmail(email)
            setMode('verify')
        } catch (err) {
            const authError = err as AuthError
            setError(getErrorMessage(authError.code))
        } finally {
            setLoading(false)
        }
    }

    const handleResendVerification = async () => {
        setLoading(true)
        try {
            const user = auth.currentUser
            if (user) {
                await sendEmailVerification(user)
            }
        } catch (err) {
            console.error('Error resending verification:', err)
        } finally {
            setLoading(false)
        }
    }

    const resetForm = () => {
        setEmail('')
        setPassword('')
        setConfirmPassword('')
        setHoneypot('')
        setError('')
        formLoadTime.current = Date.now()
    }

    const switchMode = (newMode: AuthMode) => {
        resetForm()
        setMode(newMode)
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className={`w-full max-w-md rounded-2xl bg-white p-6 shadow-xl animate-in zoom-in-95 duration-200 ${isRTL ? 'rtl' : 'ltr'}`}
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-900">
                        {mode === 'signin' && t.signIn}
                        {mode === 'signup' && t.signUp}
                        {mode === 'verify' && t.verifyEmail}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Verify Email Screen */}
                {mode === 'verify' && (
                    <div className="text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <p className="mb-4 text-slate-600">
                            {t.verifyEmailMessage.replace('{email}', verificationEmail)}
                        </p>
                        <button
                            onClick={handleResendVerification}
                            disabled={loading}
                            className="text-blue-600 hover:underline disabled:opacity-50"
                        >
                            {t.resendVerification}
                        </button>
                        <button
                            onClick={() => {
                                switchMode('signin')
                                onClose()
                            }}
                            className="mt-4 w-full rounded-xl bg-blue-600 py-3 px-4 font-bold text-white transition-colors hover:bg-blue-700"
                        >
                            OK
                        </button>
                    </div>
                )}

                {/* Sign In / Sign Up Form */}
                {(mode === 'signin' || mode === 'signup') && (
                    <>
                        {/* Google Sign In Button */}
                        <button
                            onClick={handleGoogleSignIn}
                            disabled={loading}
                            className="mb-4 flex w-full items-center justify-center gap-3 rounded-xl border border-slate-300 bg-white py-3 px-4 font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                                <path
                                    fill="#4285F4"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="#34A853"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="#FBBC05"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="#EA4335"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                            {t.continueWithGoogle}
                        </button>

                        {/* Divider */}
                        <div className="relative mb-4">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="bg-white px-2 text-slate-500">{t.orContinueWith}</span>
                            </div>
                        </div>

                        {/* Email/Password Form */}
                        <form onSubmit={mode === 'signin' ? handleEmailSignIn : handleEmailSignUp}>
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">{t.email}</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">{t.password}</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        required
                                        minLength={6}
                                    />
                                </div>
                                {mode === 'signup' && (
                                    <div>
                                        <label className="mb-1 block text-sm font-medium text-slate-700">{t.confirmPassword}</label>
                                        <input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                )}

                                {/* Honeypot field - hidden from humans, visible to bots */}
                                <div className="sr-only" aria-hidden="true">
                                    <label htmlFor="website">Website</label>
                                    <input
                                        type="text"
                                        id="website"
                                        name="website"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={honeypot}
                                        onChange={(e) => setHoneypot(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                                    {error}
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="mt-6 w-full rounded-xl bg-blue-600 py-3 px-4 font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                            >
                                {loading ? '...' : mode === 'signin' ? t.signIn : t.signUp}
                            </button>
                        </form>

                        {/* Switch Mode Link */}
                        <p className="mt-4 text-center text-sm text-slate-600">
                            {mode === 'signin' ? t.noAccount : t.alreadyHaveAccount}{' '}
                            <button
                                onClick={() => switchMode(mode === 'signin' ? 'signup' : 'signin')}
                                className="font-medium text-blue-600 hover:underline"
                            >
                                {mode === 'signin' ? t.signUpLink : t.signInLink}
                            </button>
                        </p>
                    </>
                )}
            </div>
        </div>
    )
}
